const VISITOR_ID_KEY = 'yuzhen_analytics_visitor_id';
const OPTOUT_KEY = 'yuzhen_analytics_optout';
const TRACKING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'rb_clickid',
  'gclid',
  'yclid',
  'fbclid',
  'msclkid',
] as const;

type AnalyticsEventType = 'page_view' | 'add_to_cart' | 'active_time';

type AnalyticsPayload = {
  event_type: AnalyticsEventType;
  page_path: string;
  product_id?: string;
  product_name?: string;
  duration_seconds?: number;
  metadata?: Record<string, unknown>;
};

type AnalyticsPageContext = {
  pagePath: string;
  trackingParams: Record<string, string>;
};

const createVisitorId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `visitor_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateVisitorId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const existingId = window.localStorage.getItem(VISITOR_ID_KEY);
  if (existingId) {
    return existingId;
  }

  const nextId = createVisitorId();
  window.localStorage.setItem(VISITOR_ID_KEY, nextId);
  return nextId;
};

export const getAnalyticsVisitorId = () => getOrCreateVisitorId();

// Опт-аут владельца: визит yuzhen.store/?notrack=1 выключает трекинг
// на этом браузере (?notrack=0 — включает обратно). Флаг в localStorage.
export const applyTrackingOptOutFromUrl = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('notrack');
    if (value === '1') {
      window.localStorage.setItem(OPTOUT_KEY, '1');
    } else if (value === '0') {
      window.localStorage.removeItem(OPTOUT_KEY);
    }
  } catch {
    // localStorage может быть недоступен (private mode) — игнорируем
  }
};

const isTrackingOptedOut = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(OPTOUT_KEY) === '1';
  } catch {
    return false;
  }
};

const normalizePagePath = (rawValue: string) => {
  const fallbackBase =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://yuzhen.store';

  try {
    const parsed = new URL(rawValue || '/', fallbackBase);
    let normalizedPath = parsed.pathname || '/';
    normalizedPath = normalizedPath.replace(/\/{2,}/g, '/');

    if (!normalizedPath.startsWith('/')) {
      normalizedPath = `/${normalizedPath}`;
    }

    if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
      normalizedPath = normalizedPath.replace(/\/+$/, '');
    }

    return normalizedPath || '/';
  } catch {
    const stripped = String(rawValue || '/')
      .split('#')[0]
      .split('?')[0]
      .trim();

    if (!stripped) {
      return '/';
    }

    const withLeadingSlash = stripped.startsWith('/') ? stripped : `/${stripped}`;
    const collapsed = withLeadingSlash.replace(/\/{2,}/g, '/');
    return collapsed.length > 1 && collapsed.endsWith('/') ? collapsed.replace(/\/+$/, '') : collapsed;
  }
};

const extractTrackingParams = (rawValue: string) => {
  const fallbackBase =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://yuzhen.store';

  try {
    const parsed = new URL(rawValue || '/', fallbackBase);
    const collected: Record<string, string> = {};

    TRACKING_PARAM_KEYS.forEach((key) => {
      const value = parsed.searchParams.get(key);
      if (value) {
        collected[key] = value.slice(0, 255);
      }
    });

    return collected;
  } catch {
    return {};
  }
};

export const getAnalyticsPageContext = (rawValue: string): AnalyticsPageContext => ({
  pagePath: normalizePagePath(rawValue),
  trackingParams: extractTrackingParams(rawValue),
});

export const getCurrentAnalyticsPageContext = (): AnalyticsPageContext => {
  if (typeof window === 'undefined') {
    return { pagePath: '/', trackingParams: {} };
  }

  return getAnalyticsPageContext(`${window.location.pathname}${window.location.search || ''}`);
};

const postWithBeacon = (body: string) => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.sendBeacon) {
    return false;
  }

  try {
    const blob = new Blob([body], { type: 'application/json' });
    return navigator.sendBeacon('/api/analytics/events/', blob);
  } catch {
    return false;
  }
};

const postAnalyticsEvent = (payload: AnalyticsPayload) => {
  if (typeof window === 'undefined' || isTrackingOptedOut()) {
    return;
  }

  const visitor_id = getOrCreateVisitorId();
  if (!visitor_id) {
    return;
  }

  const body = JSON.stringify({
    ...payload,
    visitor_id,
  });

  if (postWithBeacon(body)) {
    return;
  }

  fetch('/api/analytics/events/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Silent fallback: analytics should never block the storefront.
  });
};

export const trackPageView = (pagePath: string) => {
  const pageContext = getAnalyticsPageContext(pagePath);
  postAnalyticsEvent({
    event_type: 'page_view',
    page_path: pageContext.pagePath,
    metadata: {
      title: typeof document !== 'undefined' ? document.title : '',
      tracking_params: pageContext.trackingParams,
    },
  });
};

export const trackAddToCart = (args: {
  pagePath: string;
  productId: string;
  productName: string;
  quantity?: number;
  variant?: string;
  size?: string;
}) => {
  const pageContext = getAnalyticsPageContext(args.pagePath);
  postAnalyticsEvent({
    event_type: 'add_to_cart',
    page_path: pageContext.pagePath,
    product_id: args.productId,
    product_name: args.productName,
    metadata: {
      quantity: args.quantity ?? 1,
      variant: args.variant || '',
      size: args.size || '',
      tracking_params: pageContext.trackingParams,
    },
  });
};

export const trackActiveTime = (args: {
  pagePath: string;
  durationSeconds: number;
}) => {
  const durationSeconds = Math.max(0, Math.round(args.durationSeconds));
  if (durationSeconds <= 0) {
    return;
  }

  const pageContext = getAnalyticsPageContext(args.pagePath);
  postAnalyticsEvent({
    event_type: 'active_time',
    page_path: pageContext.pagePath,
    duration_seconds: durationSeconds,
    metadata: {
      tracking_params: pageContext.trackingParams,
    },
  });
};
